// content-manager.service.ts
import { Injectable } from '@angular/core';
import { Styles } from '@app/core/interfaces/styles/styles.interface';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, filter, finalize, map, raceWith, switchMap, take, tap } from 'rxjs/operators';
import {
  BlockContent,
  BlockSlider,
  PageType,
  SeoTag,
  SliderSettings,
  SliderSettingsAnimation,
  StructureContent,
  UploadPhoto,
} from '../pages/models/content.model';
import { ContentApiService } from './content-api.service';
import { ContentBlockService } from './content-block.service';
import { ContentDomService } from './content-dom.service';
import { ContentMediaApiService, MediaResponse } from './content-media-api.service';
import { ContentStorageService } from './content-storage.service';
import { ContentSynchronizationService } from './content-synchronization.service';

/**
 * Główny serwis zarządzający treścią pełniący rolę fasady dla podserwisów.
 * Koordynuje operacje między warstwą API, storage, DOM i synchronizacją.
 */
@Injectable({
  providedIn: 'root',
})
export class ContentManagerService {
  private readonly GLOBAL_STYLES_KEY = 'GlobalStyles';
  private readonly INIT_FLAG_KEY = 'contentManagerInitialized';
  private sessionCleared = false;

  constructor(
    private apiService: ContentApiService,
    private storageService: ContentStorageService,
    private blockService: ContentBlockService,
    private domService: ContentDomService,
    private syncService: ContentSynchronizationService,
    private mediaApi: ContentMediaApiService,
  ) {
    console.log('[ContentManagerService] Inicjalizacja serwisu zarządzania treścią');
    this.ensureSessionCleanup();
  }

  /**
   * Zapewnia jednorazowe czyszczenie localStorage w bieżącej sesji przed pierwszym pobraniem
   * @remarks Wykonuje się tylko raz na sesję aplikacji, nie zapisuje flagi w localStorage
   */
  private ensureSessionCleanup(): void {
    if (!this.sessionCleared && this.checkEditMode()) {
      console.log('[ContentManagerService->ensureSessionCleanup] Pierwsza operacja w sesji - rozpoczęcie czyszczenia');
      const removedCount = this.storageService.clearAllContentData(true);
      console.log(`[ContentManagerService->ensureSessionCleanup] Usunięto ${removedCount} elementów z localStorage`);
      this.sessionCleared = true;
    }
  }

  // Dodajemy metodę sprawdzającą w ContentManagerService
  private checkEditMode(): boolean {
    const isEditMode = window.location.hash.includes('edit');
    if (!isEditMode) {
      console.warn('Ta operacja wymaga trybu edycji');
    }
    return isEditMode;
  }

  // ZAPIS STRUKTUR I ODCZYT

  /**
   * Zapisuje strukturę macierzową do localStorage
   * @param content - Dane struktury macierzowej
   * @remarks Wykorzystuje ContentStorageService do zapisu dwóch wersji (original/working)
   */
  saveMatrixContent(content: BlockContent): void {
    if (!this.checkEditMode()) {
      return;
    }
    console.log('saveMatrixContent', content);
    this.storageService.saveMatrixContent(content);
  }

  /**
   * Zapisuje strukturę blokową do localStorage
   * @param content - Lista bloków treści
   * @param structure_type - Typ struktury (domyślnie 'page')
   * @remarks Automatycznie wykonuje głębokie kopiowanie struktury przed zapisem
   */
  saveBlockContent(
    content: StructureContent,
    structure_type = 'page',
  ): void {
    if (!this.checkEditMode()) {
      return;
    }
    this.storageService.saveBlockContent(content, structure_type);
  }

  // API - OPERACJE NA MEDIACH

  /**
   * Przesyła plik multimedialny do API z opcjami dostosowanymi dla komponentów
   * @param file - Plik do przesłania
   * @param options - Opcje konfiguracyjne dla procesu przesyłania i obsługi odpowiedzi
   * @param onProgress - Callback do śledzenia postępu przesyłania (0-100%)
   * @returns Observable z odpowiedzią serwera zawierającą dane przesłanego pliku
   */
  uploadMedia(
    file: File,
    options?: {
      excludeGlobalLoader?: boolean;
      includePhotosLoader?: boolean;
      isBanner?: boolean;
      updateMediaIds?: boolean; // opcja określająca, czy aktualizować identyfikatory mediów
      onSuccess?: (response: MediaResponse) => void; // callback po udanym przesłaniu
    },
    onProgress?: (percent: number) => void,
  ): Observable<MediaResponse> {
    if (!this.checkEditMode()) {
      return of();
    }
    return this.mediaApi.uploadImage(file, options || {}, onProgress).pipe(
      tap((response) => {
        console.log('[ContentManagerService->uploadMedia] response', response);
        // Aktualizuj identyfikatory mediów tylko jeśli opcja jest włączona (domyślnie true)
        // if (
        //   options?.updateMediaIds !== false &&
        //   response.oldMediaId &&
        //   response.newMediaId
        // ) {
        //   this.blockService.updateMediaIds(
        //     response.oldMediaId,
        //     response.newMediaId
        //   );
        // }

        // Wywołaj callback onSuccess, jeśli został przekazany
        if (options?.onSuccess && typeof options.onSuccess === 'function') {
          options.onSuccess(response);
        }
      }),
    );
  }

  //Przykład użycia  W komponencie

  // onFileSelected(event: Event): void {
  //   const input = event.target as HTMLInputElement;
  //   if (input.files && input.files.length > 0) {
  //     const file = input.files[0];

  //     this.contentManager.uploadMedia(
  //       file,
  //       {
  //         updateMediaIds: true,
  //         onSuccess: (response) => {
  //           this.mediaUrl = response.url;
  //           this.mediaId = response.id;
  //         }
  //       },
  //       (progress) => this.uploadProgress = progress
  //     ).subscribe({
  //       error: (err) => this.handleError(err)
  //     });
  //   }
  // }

  // API - OPERACJE NA STRUKTURACH

  /**
   * Pobiera i zapisuje strukturę blokową dla strony z czyszczeniem localStorage
   * @param pageId - ID strony
   * @remarks Automatycznie czyści localStorage przed pierwszym pobraniem w sesji
   */
  getAndSaveBlockContent(pageId: number): void {
    if (!this.checkEditMode()) {
      return;
    }

    // Wyczyść localStorage przed pierwszym pobraniem struktur
    this.ensureSessionCleanup();

    const clientId = this.storageService.findClientIdInStored();
    if (!clientId) {
      console.error('[ContentManagerService->getAndSaveBlockContent] Błąd: Brak client_id');
      return;
    }

    console.log(`[ContentManagerService->getAndSaveBlockContent] Pobieranie struktur dla strony ${pageId}`);
    this.apiService.getBlockContentFromApi(pageId, clientId).subscribe({
      next: (content) => {
        console.log('[ContentManagerService->getAndSaveBlockContent] Otrzymano struktury:', content);
        this.saveBlockContent(content);
      },
      error: (err) => {
        console.error('[ContentManagerService->getAndSaveBlockContent] Błąd podczas pobierania bloków:', err.message || err);
      },
    });
  }

  /**
   * Pobiera strukturę blokową dla konkretnego bloku z czyszczeniem localStorage
   * @param blockId - ID bloku
   * @returns Observable z listą bloków
   * @remarks Automatycznie czyści localStorage przed pierwszym pobraniem w sesji
   */
  getAndSaveBlockContentByBlock(blockId: string): Observable<any> {
    if (!this.checkEditMode()) {
      return of();
    }

    // Wyczyść localStorage przed pierwszym pobraniem struktur
    this.ensureSessionCleanup();

    console.log('[ContentManagerService->getAndSaveBlockContentByBlock] Pobieranie struktur dla bloku:', blockId);
    const clientId = this.storageService.findClientIdInStored();

    this.apiService
      .getBlocksByBlockIdFromApi(blockId, clientId)
      .pipe(
        map((structure: StructureContent) => {
          console.log('[ContentManagerService->getAndSaveBlockContentByBlock] Otrzymano strukturę bloku:', structure);
          const structureContent: StructureContent = {
            ...structure,
            structure_type: 'block',
          };
          console.log('[ContentManagerService->getAndSaveBlockContentByBlock] Przygotowana struktura do zapisu:', structureContent);
          this.storageService.saveBlockContent(structureContent, 'block');
          return structureContent;
        }),
      )
      .subscribe();

    return of();
  }

  /**
   * Pobiera tablicę tagów SEO z API
   * @returns Observable z tablicą SeoTag
   * @remarks Wynik należy obsłużyć przez .subscribe() lub async pipe w szablonie
   */
  getTags(): Observable<SeoTag[]> {
    if (!this.checkEditMode()) {
      return of([]);
    }
    // TODO: wyłaczenie pobierania tagów
    // return;
    // Wywołuje metodę serwisu API, która zwraca Observable z tablicą tagów SEO
    return this.apiService.getSeoTags();
  }

  /**
   * Pobiera globalne style z API z czyszczeniem localStorage
   * @param refresh - Flaga wymuszająca pobranie świeżych danych z API
   * @returns Observable z tablicą stylów
   * @remarks Automatycznie czyści localStorage przed pierwszym pobraniem w sesji
   */
  getGlobalStyles(refresh = false): Observable<{
    title: string;
    style_id: string;
    styles: Styles;
  }[]> {
    if (!this.checkEditMode()) {
      return of([]);
    }

    // Wyczyść localStorage przed pierwszym pobraniem globalnych stylów
    this.ensureSessionCleanup();

    // Sprawdzenie warunku cache tylko jeśli nie wymuszamy odświeżenia
    if (!refresh) {
      try {
        const cachedData = localStorage.getItem(this.GLOBAL_STYLES_KEY);
        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          if (
            Array.isArray(parsed) &&
            parsed.every((item) => item.title && item.style_id && item.styles)
          ) {
            console.log('[ContentManagerService->getGlobalStyles] Zwracam globalne style z cache');
            return of(parsed);
          }
        }
      } catch (error) {
        console.error('[ContentManagerService->getGlobalStyles] Błąd parsowania cache globalnych stylów:', error);
      }
    }

    // Pobranie globalnych stylów z API i zapis do cache
    console.log('[ContentManagerService->getGlobalStyles] Pobieranie globalnych stylów z API');
    return this.apiService.getGlobalStyles().pipe(
      tap((styles) => {
        try {
          console.log('[ContentManagerService->getGlobalStyles] Zapisywanie globalnych stylów do cache');
          localStorage.setItem(
            this.GLOBAL_STYLES_KEY,
            JSON.stringify(styles, (key, value) => {
              return key.startsWith('_') ? undefined : value;
            }),
          );
        } catch (e) {
          console.error('[ContentManagerService->getGlobalStyles] Błąd zapisu globalnych stylów do cache:', e);
        }
      }),
    );
  }

  // INTEGRACJA Z DOM

  /**
   * Przetwarza element HTML aktualizując powiązane struktury danych
   * @param element - Element DOM do analizy
   * @remarks Automatycznie wykrywa blok po klasie CSS i aktualizuje jego zawartość
   */
  processHtmlElement(element: HTMLElement): void {
    if (!this.checkEditMode()) {
      return;
    }
    console.log('[ContentManagerService->processHtmlElement]');
    this.domService.processHtmlElement(element);
  }

  /**
   * Aktualizuje elementy slidera na podstawie przekazanej listy slajdów
   * @param sliders - Lista elementów slajdów do aktualizacji/dodania
   * @param uIdSliders - Unikalny identyfikator głównego bloku slidera
   * @returns true jeśli operacja się powiodła, false w przeciwnym przypadku
   * @remarks Wymaga trybu edycji, deleguje operacje do ContentBlockService
   */
  setSlider(sliders: BlockSlider[], uIdSliders: string): boolean {
    if (!this.checkEditMode()) {
      return false;
    }

    console.log('[ContentManagerService->setSlider] Rozpoczęcie aktualizacji slidera');
    console.log('[ContentManagerService->setSlider] sliders:', sliders);
    console.log('[ContentManagerService->setSlider] uIdSliders:', uIdSliders);

    try {
      const result = this.blockService.setSliderItems(sliders, uIdSliders);
      console.log('[ContentManagerService->setSlider] Wynik operacji:', result);
      return result;
    } catch (error) {
      console.error('[ContentManagerService->setSlider] Błąd podczas aktualizacji slidera:', error);
      return false;
    }
  }

  setPhoto(photo: UploadPhoto): void {
    this.blockService.setPhoto(photo);
  }


  // SYNCHRONIZACJA DANYCH

  /**
   * Synchronizuje wszystkie zmienione struktury z backendem jako Observable.
   * Po pomyślnej synchronizacji wykonuje przeładowanie strony.
   * Błędy są przekazywane przez strumień i logowane.
   *
   * Mechanizm:
   * - Zwraca Observable<boolean> informujący o statusie operacji (true - sukces, false - błąd)
   * - W przypadku sukcesu wywołuje window.location.reload()
   * - W przypadku błędu loguje szczegóły i przekazuje błąd dalej
   *
   * @returns Observable<boolean> | throwError
   */
  synchronizeChangedStructures(): Observable<boolean> {
    if (!this.checkEditMode()) {
      console.warn('[ContentManagerService] Synchronizacja wymaga trybu edycji');
      return throwError(() => new Error('Brak uprawnień w trybie edycji'));
    }

    console.log('[ContentManagerService->synchronizeChangedStructures] Rozpoczęcie synchronizacji');

    return this.syncService.synchronizeChangedStructures().pipe(
      tap({
        next: () => {

        },
        error: (err) => console.error('[ContentManagerService] Błąd w strumieniu:', err),
      }),
      map(() => true), // Konwersja na true przy sukcesie
      catchError(error => {
        console.error('[ContentManagerService] Krytyczny błąd synchronizacji:', error);
        return of(false); // Zwracamy false zamiast rzucania błędu
      }),
      finalize(() => console.log('[ContentManagerService] Zakończenie procesu synchronizacji')),
    );
  }


  /**
   * Anuluje proces tworzenia treści poprzez wywołanie API.
   * @returns Observable z wynikiem anulowania
   */
  cancel(): Observable<any> {
    if (!this.checkEditMode()) {
      return of();
    }
    return this.apiService.cancel();
  }

  /**
   * Sprawdza istnienie niezapisanych zmian w strukturach treści
   * @returns true jeśli wykryto jakiekolwiek niezapisane zmiany
   * @remarks Wykorzystuje mechanizm głębokiego porównania struktur working/original
   */
  hasUnsavedChanges(): boolean {
    if (!this.checkEditMode()) {
      console.warn('[ContentManagerService] Próba sprawdzenia zmian poza trybem edycji');
      return false;
    }

    console.log('[ContentManagerService->hasUnsavedChanges] Wyszukiwanie niezapisanych zmian');
    const changesDetected = this.syncService.hasUnsavedChanges();
    console.log(`[ContentManagerService->hasUnsavedChanges] Znaleziono zmian: ${changesDetected}`);
    return changesDetected;
  }

  /**
   * Odrzuca wszystkie lokalne zmiany i przeładowuje stronę
   * @remarks Przywraca oryginalne wersje wszystkich struktur
   */
  discardAllBlockChangesAndReload(): void {
    if (!this.checkEditMode()) {
      return;
    }
    this.syncService.discardAllBlockChangesAndReload();
  }

  /**
   * Odrzuca zmiany dla konkretnej struktury
   * @param structureId - ID struktury do przywrócenia
   */
  discardBlockChanges(structureId: string): void {
    if (!this.checkEditMode()) {
      return;
    }
    this.syncService.discardBlockChanges(structureId);
  }

  /**
   * Pobiera ID ostatnio modyfikowanej struktury
   * @returns ID struktury w formacie string
   */
  getCurrentStructureId(): string {
    if (!this.checkEditMode()) {
      return;
    }
    return this.blockService.getCurrentStructureId();
  }

  /**
   * Pobiera pierwszą znalezioną strukturę strony z wersją roboczą
   * @returns Observable emitujący strukturę strony lub błąd po 30 sekundach
   * @remarks Filtruje po kluczach 'block_*_working' i strukturze typu 'page'
   */
  getPageSettings(): Observable<StructureContent> {
    if (!this.checkEditMode()) {
      return throwError(() => new Error('Nie jesteś w trybie edycji'));
    }

    const MAX_RETRY_TIME = 30000; // 30 sekund
    const POLL_INTERVAL = 500; // co 500ms sprawdzaj dostępność

    const timeout$ = timer(MAX_RETRY_TIME).pipe(
      switchMap(() => throwError(() => new Error('Nie udało się pobrać struktur w ciągu 30 sekund'))),
    );

    return timer(0, POLL_INTERVAL).pipe(
      tap(attempt => console.log(`[ContentManagerService->getPageSettings] Próba pobrania struktury (${attempt + 1})`)),
      map(() => {
        const result = this.storageService.getPageSettingsFromStructure();
        console.log('[ContentManagerService->getPageSettings] Wynik pobierania:', result ? 'sukces' : 'brak danych');
        return result;
      }),
      filter((data): data is StructureContent => data !== null),
      take(1),
      raceWith(timeout$),
      finalize(() => console.log('[ContentManagerService->getPageSettings] Zakończono próby pobierania')),
    );
  }


  setPageSetting(settings: StructureContent): void {
    if (!this.checkEditMode()) {
      return;
    }
    this.storageService.savePageSettingsToStructure(settings);
  }

  /**
   * Pobiera blok typu slider o określonym identyfikatorze
   * @param blockId - ID bloku do wyszukania
   * @returns Znaleziony blok-slider lub null
   */
  getSliderBlock(blockId: string): BlockContent | null {
    if (!this.checkEditMode()) {
      return null;
    }
    return this.blockService.getSliderBlock(blockId);
  }


  getSliders(blockId: string): BlockSlider[] | null {
    if (!this.checkEditMode()) {
      return null;
    }
    return this.blockService.getSliders(blockId);
  }

  /**
   * Pobiera ustawienia slidera z bloku o podanym ID
   * @param blockId - Unikalny identyfikator bloku slidera
   * @returns Ustawienia slidera lub null jeśli blok nie istnieje lub nie jest sliderem
   */
  getSliderSettings(blockId: string): SliderSettings | null {
    if (!this.checkEditMode()) {
      return null;
    }
    return this.blockService.getSliderSettings(blockId);
  }

  /**
   * Zapisuje ustawienia slidera do bloku o podanym ID
   * @param blockId - Unikalny identyfikator bloku slidera
   * @param settings - Nowe ustawienia slidera
   * @returns true jeśli zapis się powiódł, false w przeciwnym przypadku
   */
  setSliderSettings(
    blockId: string,
    settings: SliderSettingsAnimation,
  ): boolean {
    if (!this.checkEditMode()) {
      return false;
    }
    return this.blockService.setSliderSettings(blockId, settings);
  }

  /**
   * Pobiera listę typów stron z API
   * @returns Observable z tablicą PageType
   * @remarks Wykorzystuje istniejący mechanizm autoryzacji z ContentApiService
   */
  getPageTypes(): Observable<PageType[]> {
    if (!this.checkEditMode()) {
      return of([]);
    }

    console.log('[ContentManagerService->getPageTypes] Inicjowanie pobierania typów stron');
    return this.apiService.getPageTypes().pipe(
      tap(types => console.debug('Otrzymano typy stron:', types)),
      catchError(error => {
        console.error('Błąd pobierania typów stron:', error);
        return throwError(() => error);
      }),
    );
  }
}
